import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ErrorService } from '@app/common/services/error.service';
import { PlexEnvironment } from '@app/env';
import { WrappedHttpErrorResponse } from "@app/errors/wrapped-error";
import { ExistingItemInfo, IItemDto } from "@app/shared/models/dtos/dtos";
import { IItemCategoryDto } from '@app/shared/models/dtos/item-category';
import { IItemScopingCategoryDto } from '@app/shared/models/dtos/item-scoping-category';
import { IItemSubCategoryDto } from '@app/shared/models/dtos/item-sub-category';
import { IScopingDependentItemDto } from '@app/shared/models/dtos/scoping-dependent-item';
import { Observable } from "rxjs";
import { throwError as observableThrowError } from "rxjs/internal/observable/throwError";
import { catchError, map } from "rxjs/operators";
import { IScopingItemGroupMapping } from '../scoping-items/models/iscoping-item-group-mapping';
import { ScopingItemGroupMapping } from '../scoping-items/models/scoping-item-group-mapping';

@Injectable({
  providedIn: 'root'
})
export class ItemManagementService {
  private http = inject(HttpClient);
  private errorService = inject(ErrorService);
  public environment = inject(PlexEnvironment);

  private BASE_URL = '';

  constructor() {
    this.BASE_URL = this.environment.apiUrl;
  }

  public async findItemDtos(): Promise<ItemsDtoResponse> {
    let url = `${this.BASE_URL}itemMaintenance/finditemdtos`;
    return this.http.get<ItemsDtoResponse>(url).toPromise();
  }

  public async findItemScopingTypeDtos(): Promise<ItemScopingCategoryDtoResponse> {
    let url = `${this.BASE_URL}lookup/finditemscopingcategories`;
    return this.http.get<ItemScopingCategoryDtoResponse>(url).toPromise();
  }

  public async findItemTypeDtos(): Promise<ItemCategoryDtoResponse> {
    let url = `${this.BASE_URL}lookup/finditemcategories`;
    return this.http.get<ItemCategoryDtoResponse>(url).toPromise();
  }

  public async findItemSubTypeDtos(): Promise<ItemSubCategoryDtoResponse> {
    let url = `${this.BASE_URL}lookup/finditemsubcategories`;
    return this.http.get<ItemSubCategoryDtoResponse>(url).toPromise();
  }

  public getScopingItemGroupMappings(): Promise<IScopingItemGroupMapping[]> {
    let url = `${this.BASE_URL}itemMaintenance/GetScopingItemGroupMappings`;
    return this.http.get(url).pipe(
      map((response: any) => response as IScopingItemGroupMapping[]),
      catchError(error => this.handleErrorObservable(error, 'Error getting Scoping Item Groups.'))).toPromise();
  }

  public async getScopingDependentItems(id: number): Promise<IScopingDependentItemDto[]> {
    let url = `${this.BASE_URL}itemMaintenance/GetScopingDependentItems/${id}`
    return this.http.get<IScopingDependentItemDto[]>(url).pipe(
      catchError(error => this.handleErrorObservable(error, 'Error getting Scoping Item Groups.'))).toPromise();
  }

  public saveItemGroupMappings(groups: ScopingItemGroupMapping[]) {
    let url = `${this.BASE_URL}itemMaintenance/SaveScopingItemGroupMappings`;
    return this.http.post<ScopingItemGroupMapping[]>(url, groups).pipe(
      catchError((e) => { return this.errorService.handleHTTPError(e); }));
  }

  public ifItemsExist(ifItemsExist: Array<IItemDto>): Observable<Array<ExistingItemInfo>> {
    let url = `${this.BASE_URL}itemMaintenance/IfItemsExist`;
    return this.http.post<Array<ExistingItemInfo>>(url, ifItemsExist);
  }

  public upsertItemDtos(upsertItems: Array<IItemDto>): Observable<ItemsDtoResponse> {
    let url = `${this.BASE_URL}itemMaintenance/upsertitems`;
    return this.http.post<ItemsDtoResponse>(url, upsertItems);
  }

  public getItemDtoByIdAsync(itemId: number): Observable<ItemsDtoSingleResponse> {

    let url = `${this.BASE_URL}itemMaintenance/getitemdtobyid/` + itemId;
    return this.http.get<ItemsDtoSingleResponse>(url);
  }

  private handleErrorObservable(error: Response | any, message: string) {
    return observableThrowError(new WrappedHttpErrorResponse(message, error));
  }
}
export interface ItemsDtoSingleResponse {
  Result: IItemDto;
}

export interface ItemsDtoResponse {
  Results: Array<IItemDto>;
}

export interface ItemScopingCategoryDtoResponse {
  Results: Array<IItemScopingCategoryDto>;
}

export interface ItemCategoryDtoResponse {
  Results: Array<IItemCategoryDto>;
}

export interface ItemSubCategoryDtoResponse {
  Results: Array<IItemSubCategoryDto>;
}